/* Patch monitoring — nav gating and column selection. */

import { USER_ROLES } from "static/constants";

/* Patch vendors. Mirrors PATCH_MANUFACTURERS on the API side and `patchManufacturers` in
 * static/manufacturers.js — the list the user modal offers. */
export const PATCH_MANUFACTURERS = ["Wearlinq"];

export const PATCH_ROUTE = "/patch-monitoring";

/**
 * Whether the current location is the patch worklist. Shared components read this to
 * withhold actions that do not apply here.
 */
export const isPatchMonitoringRoute = (pathname) =>
  typeof pathname === "string" && pathname.startsWith(PATCH_ROUTE);

/* Roles allowed on the page. Clinic-side only for now — Evoke staff never touch these
 * reports, which is why the route's authorizedRoles and the nav gate agree. Widening
 * this to Evoke admins for support triage is deferred decision D2. */
export const PATCH_ROLES = ["clinic", "clinicAdmin", "physician"];

/* The three clinic stages, in workflow order. Values are the `view` param the existing
 * transmissions endpoints already understand, so no new endpoint is involved. */
export const PATCH_TABS = [
  { label: "Active", value: "clinic" },
  { label: "Review", value: "clinic-review" },
  { label: "Signed/Archived", value: "clinic-processed" },
];

// Physicians start on Review and everyone else on Active, as on the Transmissions page.
export const defaultPatchView = (userRole) =>
  userRole === USER_ROLES.PHYSICIAN ? "clinic-review" : PATCH_TABS[0].value;

// The tab named in the URL, e.g. /patch-monitoring/clinic-review; the role's default when absent or unknown.
export const readPatchView = (pathname, userRole) => {
  const segment = String(pathname || "")
    .slice(PATCH_ROUTE.length)
    .split("/")
    .filter(Boolean)[0];

  return PATCH_TABS.some((tab) => tab.value === segment)
    ? segment
    : defaultPatchView(userRole);
};

export const patchTabRoute = (view) => `${PATCH_ROUTE}/${view}`;

// Page title in the same shape as Transmissions, e.g. "CAET - Patch Monitoring Review Transmissions".
export const buildPatchTitle = (view, clinicName) => {
  const label = PATCH_TABS.find((tab) => tab.value === view)?.label;
  const title = `Patch Monitoring ${label ? `${label} ` : ""}Transmissions`;

  return clinicName && clinicName !== "None"
    ? `${clinicName} - ${title}`
    : title;
};

/* The columns the patch grid shows.
 *
 * These are column keys, not report field names — the two differ, and a key that matches
 * nothing drops the column silently.
 */
export const PATCH_COLUMNS = [
  "visibility",
  "date",
  "name",
  "mrn",
  "account",
  "manufacturer",
  "deviceType",
  "reportNotes",
  "assignedPhysician",
  "returned",
  "eSig",
  "signedBy",
  "signedAt",
];

/* Columns hidden here: billing, EHR export, device alerts and implant-population fields,
 * none of which a patch report carries. Every column key belongs to one list or the other.
 */
export const DROPPED_COLUMNS = [
  "billable",
  "lastBillDate",
  "nextSend",
  "clinic_billed",
  "oru",
  "dft",
  "device_alert",
  "oac",
  "hospital",
];

// Trimmed, case-insensitive match against the patch vendors.
const isPatchManufacturer = (manufacturer) =>
  PATCH_MANUFACTURERS.some(
    (patch) => String(manufacturer).trim().toLowerCase() === patch.toLowerCase()
  );

/**
 * Whether a report comes from a patch vendor (Wearlinq), i.e. belongs to Patch Monitoring.
 */
export const isPatchReport = (report) =>
  Boolean(report?.manufacturer) && isPatchManufacturer(report.manufacturer);

/**
 * Where to send a user after leaving a patch report when there is no tab to go back to:
 * the role's default Patch Monitoring tab, or null for roles outside Patch Monitoring.
 */
export const getPatchFallbackRoute = (userRole) =>
  PATCH_ROLES.includes(userRole)
    ? patchTabRoute(defaultPatchView(userRole))
    : null;

/**
 * Whether the nav entry should render: the account has patch monitoring, the user holds a
 * patch manufacturer, and the role is clinic-side. The API enforces the same three.
 */
export const shouldShowPatchNav = ({
  currentUser,
  accountsMapper,
  clinicName,
  isRPMSaaSUser = false,
} = {}) => {
  if (isRPMSaaSUser) return false;

  const accountEnabled = Boolean(
    accountsMapper?.[clinicName]?.settings?.patch_monitoring?.enabled
  );

  const held = Array.isArray(currentUser?.manufacturer)
    ? currentUser.manufacturer
    : [];

  const userHoldsAPatchManufacturer = held.some(isPatchManufacturer);

  return (
    accountEnabled &&
    userHoldsAPatchManufacturer &&
    PATCH_ROLES.includes(currentUser?.role)
  );
};

/**
 * Whether a column belongs on the patch grid.
 */
export const isPatchColumn = (columnId) => PATCH_COLUMNS.includes(columnId);
